import { FeedbackFormQuestionType } from 'types'
import { Card } from 'types/card'
import { CoreLessonStatusEnum, CoreEntryEnum, CoreExitEnum } from 'scorm/types'

export type EndUserAgreement = {
  agreementText: string
  descriptionText: string
}

export type ElearningChapter = {
  chapterId: number
  name: string
  estimation: Nullable<string>
  isAssessment: boolean
  isActive: boolean
  score: Nullable<number>
  timeSpent: Nullable<string>
  lessonStatus: CoreLessonStatusEnum
}

export type ElearingScormData = {
  isResponsive: boolean
  canBeResized: boolean
  canBeMaximized: boolean
  width: number
  height: number
  masteryScore: number
}

export type ElearningDetailsRequirementStatusExpiryDefinition = {
  expiryDate: DateTime
  expiryType: number // TODO: Dictionaries?
}

// 0 NotApproved, 1 Approved
export enum CompletionStatus {
  NotApproved,
  Approved,
}

export type ElearningDetailsRequirementStatusItem = {
  proficiencyName: string
  minimumCompletionPercentage: number
  minimumScore: number
  expiryDefinition: ElearningDetailsRequirementStatusExpiryDefinition
  status: CompletionStatus
  parentCompetenceId?: string
}

export type ElearningDetailsRequirementStatus = {
  items: ElearningDetailsRequirementStatusItem[]
}

export type ElearningDetailsResult = {
  id: Guid
  languageKey?: string
  score: number
  completion: number
  isCompleted: boolean
  totalTime: string
  isElearningSessionStarted: boolean
}

export enum ElearningType {
  Elearning,
  QuickGuide,
  Video,
}

export type Sequencing = {
  isLocked: boolean
  blockingBy: Nullable<string>
}

export type ElearningDetailsResponse = {
  moduleId: Guid
  moduleName: string
  trainingNumber: string
  minimumScore: number
  minimumCompletion?: string
  supportsSequencing: boolean
  listOfLanguages: string[]
  showFeedbackForm: boolean
  currentReleaseNumber?: number
  chapters: ElearningChapter[]
  scormData: ElearingScormData
  result?: ElearningDetailsResult
  retractionInfo?: ElearningRetractionInfo
  endUserAgreement: EndUserAgreement
  requirementStatus?: ElearningDetailsRequirementStatus
  type: ElearningType
  description: string
  imageTimeStamp: number
  targetGroups: string[]
  relatedTrainings: Card[]
  sequencing: Sequencing
  duration?: string
  preWarningMessage?: string
}

export type CreateResultResponse = {
  resultId: Guid
}

export type ApiStartElearningSessionResponse = {
  startElearning: ScormRunResponse
}

export type ScormRunResponse = {
  elearningSessionId: Guid
  scormData: ScormRunData
}

export type ScormRunData = {
  sessionTime: string // timespan?
  studentId: string
  studentName: string
  lessonStatus: CoreLessonStatusEnum
  totalTime: string // timespan?
  lessonLocation: string
  entry: CoreEntryEnum
  exit: CoreExitEnum
  launchData: string
  scoreRaw: number
  suspendData: string
  scormLocation: string
}

export type ElearningProgressUpdateDto = {
  resultId: Guid
  scormData: ScormDataDto
  chapterId: number
  elearningSessionId: Guid
  isFinish: boolean
}

export type ScormDataDto = {
  sessionTime: string
  lessonStatus: CoreLessonStatusEnum
  totalTime: string
  lessonLocation: string
  exit: CoreExitEnum
  scoreRaw: number
  suspendData: string
  launchData: string
}

export enum ElearningResultStatus {
  NoResult,
  InProgress,
  Completed,
}

/* Feedback */
type ElearningFeedbackFormQuestionAnswer = {
  answerId: string
  answerText: string
}

export type ElearningFeedbackFormQuestion = {
  questionAnswers: ElearningFeedbackFormQuestionAnswer[]
  questionId: string
  questionText: string
  type: FeedbackFormQuestionType
}

export type ElearningFeedbackFormResponse = {
  canSkipFeedback: boolean
  introductionText: string
  questions: ElearningFeedbackFormQuestion[]
}

export type FeedbackAnswer = {
  answerId: string
  questionId: string
}

export type FeedbackFreeTextAnswer = {
  freeTextAnswerContent: string
  questionId: string
}

export type ElearningRetractionInfo = {
  isActive: boolean
  hasContent: boolean
  questionText: ElearningRetractionQuestion
  actions: ElearningRetractionAction[]
}

type ElearningRetractionQuestion = {
  preMessage: string
  questionTags: string[]
}

export type ElearningRetractionAction = {
  titleTag: string
  command: ElearningRetractionCommand
}

export enum ElearningRetractionCommand {
  Cancel,
  ProceedToTraining,
  StartOver,
  No,
  Ok,
  SaveAndClose,
  SaveAndStartNew,
  OkRemoveResult,
  DeleteResultAndStartNewSession,
}
